import { ColumnDef } from '@tanstack/react-table'
import { format, formatDistanceToNow } from 'date-fns'
import { Badge } from '@/components/ui/badge'
import { DataTableColumnHeader } from '@/components/shared/data-table/data-table-column-header'
import { ErpImageThumb } from './ErpImageThumb'
import { MappingShapeThumb } from './MappingShapeThumb'
import { paramChips, parseParams } from '../params'
import { isNonStandardMapping, type ErpShapeMappingRow } from '../types'
import { NonStandardShapeBadge } from '@/components/shared/NonStandardShapeBadge'

export const STATUS_BADGE: Record<
  string,
  { label: string; className: string }
> = {
  MAPPED: {
    label: 'Mapped',
    className: 'bg-green-100 text-green-800 hover:bg-green-100',
  },
  UNMAPPED: {
    label: 'Unmapped',
    className: 'bg-amber-100 text-amber-900 hover:bg-amber-100',
  },
  IGNORED: {
    label: 'Ignored',
    className: 'bg-gray-100 text-gray-600 hover:bg-gray-100',
  },
}

export const columns: ColumnDef<ErpShapeMappingRow>[] = [
  {
    id: 'erp_image',
    header: 'ERP image',
    cell: ({ row }) => (
      <ErpImageThumb
        thumbUrl={row.original.erp_image_thumb_url}
        fullUrl={row.original.erp_image_url}
        title={row.original.erp_schematic_id}
      />
    ),
    enableSorting: false,
    meta: { width: '90px' },
  },
  {
    accessorKey: 'erp_schematic_id',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Schematic ID" />
    ),
    cell: ({ row }) => (
      <span className="font-mono text-xs font-medium">
        {row.original.erp_schematic_id}
      </span>
    ),
    enableSorting: false,
  },
  {
    id: 'shape',
    header: 'Our shape',
    cell: ({ row }) => (
      <MappingShapeThumb
        type={row.original.sofa_form_type}
        params={parseParams(row.original.default_parameters)}
      />
    ),
    enableSorting: false,
    meta: { width: '110px' },
  },
  {
    accessorKey: 'sofa_form_type',
    header: 'Type',
    cell: ({ row }) =>
      row.original.sofa_form_type ? (
        <div className="flex flex-col items-start gap-1">
          <span className="font-mono text-xs">
            {row.original.sofa_form_type}
          </span>
          {isNonStandardMapping(row.original) && <NonStandardShapeBadge />}
        </div>
      ) : (
        <span className="text-xs text-muted-foreground">—</span>
      ),
    enableSorting: false,
  },
  {
    id: 'params',
    header: 'Parameters',
    cell: ({ row }) => {
      const chips = paramChips(parseParams(row.original.default_parameters))
      if (!chips.length)
        return <span className="text-xs text-muted-foreground">—</span>
      return (
        <div className="flex max-w-[260px] flex-wrap gap-1">
          {chips.map(chip => (
            <Badge
              key={chip}
              variant="secondary"
              className="whitespace-nowrap text-[10px] font-normal"
            >
              {chip}
            </Badge>
          ))}
        </div>
      )
    },
    enableSorting: false,
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => {
      const badge = STATUS_BADGE[row.original.status]
      return badge ? (
        <Badge className={badge.className}>{badge.label}</Badge>
      ) : (
        row.original.status
      )
    },
    enableSorting: false,
    meta: { width: '100px' },
  },
  {
    id: 'erp_usage',
    header: 'In ERP',
    cell: ({ row }) => {
      const { erp_item_count, last_seen_in_erp_at } = row.original
      if (erp_item_count == null && !last_seen_in_erp_at)
        return <span className="text-xs text-muted-foreground">—</span>
      return (
        <div className="text-xs">
          <div>{erp_item_count ?? 0} items</div>
          {last_seen_in_erp_at && (
            <div
              className="text-muted-foreground"
              title={format(new Date(last_seen_in_erp_at), 'yyyy-MM-dd HH:mm')}
            >
              {formatDistanceToNow(new Date(last_seen_in_erp_at), {
                addSuffix: true,
              })}
            </div>
          )}
        </div>
      )
    },
    enableSorting: false,
    meta: { width: '110px' },
  },
  {
    accessorKey: 'updatedAt',
    header: 'Updated',
    cell: ({ row }) => (
      <span className="text-xs text-muted-foreground">
        {format(new Date(row.original.updatedAt), 'yyyy-MM-dd HH:mm')}
      </span>
    ),
    enableSorting: false,
    meta: { width: '120px' },
  },
]
